import { Briefcase, Cloud, MessageCircle, CreditCard, Truck, Plug } from 'lucide-react';
import { PillarPage } from './PillarPage';
import { ZohoBadge, MicrosoftBadge, InteraktBadge } from '../../components/PartnerBadges';

const services = [
  {
    icon: Briefcase,
    title: 'Zoho Implementation',
    description:
      'End-to-end Zoho deployment — CRM, Books, Inventory, Desk, and the rest of the Zoho One suite — configured around your real-world processes.',
    highlights: [
      'Zoho CRM — pipelines, workflows & automation',
      'Zoho Books — accounting & invoicing',
      'Zoho Inventory & Zoho Desk',
      'Cross-module automation & reporting',
    ],
  },
  {
    icon: Cloud,
    title: 'Microsoft 365 & Azure Solutions',
    description:
      'As a Microsoft Partner, we plan and deploy Microsoft 365 and Azure solutions — from identity and Intune to Defender, Power Platform, and beyond.',
    highlights: [
      'Microsoft 365 deployment & administration',
      'Azure architecture & migration',
      'Identity & Access (Entra ID, Intune)',
      'Power Automate & Power BI integrations',
    ],
  },
  {
    icon: MessageCircle,
    title: 'Interakt WhatsApp Marketing',
    description:
      'Turn WhatsApp into a marketing and support channel — campaigns, automation, chatbots, and CRM-synced conversations powered by Interakt.',
    highlights: [
      'WhatsApp Business API setup',
      'Broadcast campaigns & segmentation',
      'Chatbot flows & auto-replies',
      'CRM-synced customer conversations',
    ],
  },
  {
    icon: CreditCard,
    title: 'Payment Gateway Integrations',
    description:
      'Integrate the right payment gateways for your region and product — from regional providers to Stripe, PayPal, KNET, and beyond.',
    highlights: [
      'Regional gateways (KNET, Tap, MyFatoorah)',
      'Global processors (Stripe, PayPal)',
      'Subscription & recurring billing',
      'Reconciliation & fraud rules',
    ],
  },
  {
    icon: Truck,
    title: 'Logistics & Delivery Integrations',
    description:
      'Connect your store or system to local and global logistics providers — automated label generation, live tracking, and real-time order routing.',
    highlights: [
      'Local & global courier integrations',
      'Automated label generation',
      'Real-time tracking & status webhooks',
      'Order routing & fulfillment automation',
    ],
  },
  {
    icon: Plug,
    title: 'Custom API Development & Middleware',
    description:
      'When off-the-shelf connectors fall short, we build the middleware that ties your systems together — reliably, securely, and with full observability.',
    highlights: [
      'Custom REST / GraphQL connectors',
      'Middleware & data sync layers',
      'Webhook handling & queueing',
      'Retry, error-handling & monitoring',
    ],
  },
];

const process = [
  { step: '01', label: 'Map',      desc: 'We document the systems, data flows, and outcomes that matter to you.' },
  { step: '02', label: 'Design',   desc: 'Integration architecture and data contracts agreed before any code.' },
  { step: '03', label: 'Build',    desc: 'We implement, test, and monitor the connectors — with rollback plans baked in.' },
  { step: '04', label: 'Validate', desc: 'End-to-end testing across edge cases, with stakeholder sign-off.' },
  { step: '05', label: 'Operate',  desc: 'Ongoing monitoring, alerting, and iteration as your systems evolve.' },
];

const faqs = [
  {
    question: 'What is Zoho One and do you implement it?',
    answer: 'Zoho One is the unified Zoho suite covering CRM, Books, Inventory, Desk, Projects, and 40+ other business apps. Yes, we implement Zoho One end-to-end — from CRM pipelines and Books accounting to inventory, helpdesk, and cross-module automation tailored to your processes.',
  },
  {
    question: 'How do you price integration projects?',
    answer: 'Integration pricing depends on the number of systems, the complexity of the data mappings, and the reliability requirements. We scope every project before committing to a price — a simple two-system sync looks very different from a multi-ERP enterprise flow. Book a call and we will scope it with you.',
  },
  {
    question: 'How long does an integration project take?',
    answer: 'A point-to-point integration with well-documented APIs takes 2 to 4 weeks. Multi-system integrations with custom business logic and reconciliation take 6 to 12 weeks. Enterprise ERP integrations (NetSuite, SAP, Dynamics) often run longer because of testing depth and stakeholder cycles.',
  },
  {
    question: 'Can you connect Shopify, Zoho, and our accounting system?',
    answer: 'Yes. We build custom integrations that sync orders, customers, inventory, and financial data across Shopify, Zoho Books, QuickBooks, Xero, and other ERP or accounting systems — using their APIs and reliable middleware with retry, error-handling, and monitoring built in.',
  },
  {
    question: 'Do you build custom integrations or just use Zapier and Make?',
    answer: 'Both — depending on what fits. Low-code platforms like Zapier, Make, and n8n are great for simple flows and fast iteration. We move to custom-coded integrations when you need transactional reliability, complex transformations, high volume, or audit trails that low-code tools cannot guarantee. We will recommend the right tool, not the most expensive one.',
  },
  {
    question: 'Do you set up WhatsApp Business API integrations?',
    answer: 'Yes. We are an Interakt partner and implement WhatsApp Business API for marketing automation, customer support, order notifications, and abandoned-cart recovery — fully integrated with your CRM and e-commerce platform.',
  },
  {
    question: 'What payment gateways do you integrate?',
    answer: 'We integrate global gateways like Stripe, PayPal, and Adyen as well as regional gateways including Razorpay, PayTabs, Telr, Network International, and Checkout.com. We also handle subscription billing, refunds, webhooks, and reconciliation.',
  },
  {
    question: 'Can you integrate logistics and shipping providers?',
    answer: 'Yes. We integrate ShipStation, Shiprocket, Aramex, DHL, FedEx, UPS, and regional carriers — covering rate shopping, label generation, tracking webhooks, and returns workflows. We also build custom logic for multi-warehouse routing and zone-based fulfillment rules.',
  },
  {
    question: 'How do you handle integration reliability?',
    answer: 'Every integration we build includes retry logic, dead-letter queues, error alerting, and monitoring dashboards. We also document the data contracts so future changes do not silently break the flow.',
  },
  {
    question: 'What happens when a third-party API changes or breaks?',
    answer: 'We monitor for upstream changes, subscribe to vendor changelogs and deprecation notices, and version our integrations so a breaking change in one system does not cascade. Retainer clients get proactive fixes; one-off projects can opt into a maintenance plan or a per-incident support agreement.',
  },
  {
    question: 'Can you migrate data between systems as a one-off project?',
    answer: 'Yes. We handle one-time data migrations — CRM-to-CRM, ERP-to-ERP, e-commerce platform changes, accounting system swaps — with full data mapping, validation passes, dry-run cutovers, and reconciliation reports against the source system.',
  },
  {
    question: 'Do you offer managed integration services?',
    answer: 'Yes. We offer monthly retainers covering integration uptime monitoring, error triage, vendor liaison, and continuous improvement. This is the right model for businesses where downstream systems break revenue or operations when an integration fails.',
  },
];

export const IntegrationsPage = () => (
  <PillarPage
    meta={{
      title: 'Integrations & Partnerships',
      label: 'Integrations & Partnerships',
      headline: 'Connect Every System You Run On',
      italicWord: 'Every System',
      description:
        'Zoho, Microsoft, Interakt WhatsApp, payment gateways, logistics, and the custom middleware that makes them all talk to each other — built and operated reliably.',
      servicesHeading: 'Integration Capabilities',
    }}
    badgeRow={<><ZohoBadge /><MicrosoftBadge /><InteraktBadge /></>}
    theme={{
      primary: '#5B21B6',
      accent:  '#8B5CF6',
      bg:      '#EDE9FE',
      soft:    '#F5F3FF',
    }}
    services={services}
    process={process}
    cta={{
      headline: 'Tired of systems that don\'t talk to each other?',
      italic:   "Let's connect them.",
      buttonLabel: 'Start an Integration Project',
    }}
    seo={{
      title: 'Zoho, Microsoft & Custom System Integrations',
      description: 'End-to-end integrations across Zoho, Microsoft 365, Azure, WhatsApp Business API, payment gateways, and logistics platforms. Reliable middleware with monitoring, retries, and error-handling.',
      path: '/services/integrations',
      serviceName: 'System Integration Services',
    }}
    faqs={faqs}
  />
);
